import { useCallback, useEffect } from 'react'
import { useFormik } from 'formik'
import { useLoginMutation, useAuthErrorMessage } from '@hooks/auth'
import { useModalsContext } from '@context/modalContext'
import usePasswordVisibility from '@hooks/usePasswordVisibility'
import { useNotifications } from '@context/notifications'
import * as yup from 'yup'
import { addToDataLayer_nextgen } from '@helpers/google-tag-manager'
import { identifyUser } from '@components/integrations/Segment'
import { getAuth, sendEmailVerification } from 'firebase/auth'
import { useTheme } from '@mui/material'

export const validationSchema = yup.object({
  email: yup
    .string('Enter your email')
    .email('Enter a valid email')
    .required('Email is required'),
  password: yup.string('Enter your password'),
})

export const useLoginForm = () => {
  const { actions } = useModalsContext()
  const { enqueue } = useNotifications()
  const theme = useTheme()

  const sendVerificationEmail = currentUserAuth => {
    sendEmailVerification(currentUserAuth)
      .then(
        actions.genericModal({
          overrideState: {
            componentData: {
              title: 'Email Verificaiton',
            },
            styles: {
              '.MuiPaper-root': {
                maxWidth: '450px',
                maxHeight: '265px',
                overflow: 'hidden',
                [theme.breakpoints.down('sm')]: {
                  maxWidth: '100%',
                  maxHeight: '100%',
                },
              },
            },
          },
          data: { PredefinedComponentModal: 'email_verification' },
        }),
      )
      .catch(error => {
        const errorCode = error.code
        const errorMessage = error.message
        console.error(errorCode, errorMessage)
      })
  }

  const onSuccess = useCallback(
    newUser => {
      actions.close()

      // Segment Identify call
      const subsourcecode = 'rtgloginaccountmodal'
      identifyUser({
        email: newUser?.contact?.email,
        subsourcecode,
      })

      addToDataLayer_nextgen('sign_in', { auth_user_id: newUser._id, method: 'email' })

      const auth = getAuth()
      if (!auth.currentUser.emailVerified && auth.currentUser) {
        setTimeout(() => {
          enqueue({
            variant: 'sectioned',
            body: 'Please verify your email for full access to your Rooms To Go account',
            severity: 'info',
            sectionedColors: {
              primary: '#0053a0',
              secondary: '#E7EFF6',
            },
            button: {
              text: 'VERIFY EMAIL',
              handleClick: () => sendVerificationEmail(auth.currentUser),
            },
          })
        }, 500)
      } else {
        setTimeout(() => {
          enqueue({ variant: 'filled', body: 'Logged in successfully' })
        }, 500)
      }
      clearTimeout()
    },
    // eslint-disable-next-line
    [actions, enqueue],
  )

  const { mutate, error, isLoading } = useLoginMutation({ onSuccess })
  const { errorMessage, clearMessage } = useAuthErrorMessage(error)
  const { showPassword, onVisibilityToggle } = usePasswordVisibility()

  const onSubmit = useCallback(
    values => {
      clearMessage()
      mutate({ values })
    },
    [clearMessage, mutate],
  )

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema,
    onSubmit,
  })

  useEffect(() => {
    if (errorMessage) {
      addToDataLayer_nextgen('account_modal_error', {
        account_modal_name: 'Log In',
        account_error_type: errorMessage,
      })
    }
  }, [errorMessage])

  return { showPassword, onVisibilityToggle, formik, loading: isLoading, errorMessage }
}
